import API_BASE from '../../utils/api';
import { formatLocalDate } from '../../utils/date';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ProfileDropdown from "../../components/ProfileDropdown";
import QrScannerModal from "../../components/QrScannerModal";
import Modal from "../../components/Modal";

const TeacherAttendance = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [autoSave, setAutoSave] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    onLeave: 0,
  });
  const messageTimeoutRef = useRef(null);

  // Period-wise marking: 0 = whole day/homeroom (default, unchanged behavior)
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { qrImage, className, section, period, expiresInMinutes }
  const [generatingQr, setGeneratingQr] = useState(false);

  useEffect(() => {
    fetchClassAndStudents();
  }, []);

  useEffect(() => {
    if (classInfo) {
      fetchExistingAttendance();
      fetchPeriods();
    }
  }, [selectedDate, classInfo, period]);

  const fetchPeriods = async () => {
    try {
      const res = await axios.get(`${API_BASE}/attendance/periods/${classInfo._id}?date=${formatLocalDate(selectedDate)}`);
      setPeriods(res.data);
    } catch (_) {
      setPeriods([]);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 's' || e.key === 'S') {
          e.preventDefault();
          handleSubmit(e);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [attendance, students]);

  const fetchClassAndStudents = async () => {
    try {
      const classResponse = await axios.get(
        `${API_BASE}/classes/my-class`,
      );
      setClassInfo(classResponse.data);

      const studentsResponse = await axios.get(
        `${API_BASE}/classes/my-class/students`,
      );
      setStudents(studentsResponse.data);

      // Initialize attendance object
      const initialAttendance = {};
      studentsResponse.data.forEach((student) => {
        initialAttendance[student._id] = "present";
      });
      setAttendance(initialAttendance);
    } catch (error) {
      console.error("Error fetching data:", error);
      showMessage("Error loading students", "error");
    } finally {
      setLoading(false);
    }
  };

  const statusToStatKey = (status) =>
    status === "half-day" ? "halfDay" : status === "on-leave" ? "onLeave" : status;

  const fetchExistingAttendance = async () => {
    try {
      const formattedDate = formatLocalDate(selectedDate);
      const response = await axios.get(
        `${API_BASE}/attendance/class/${classInfo._id}?date=${formattedDate}&period=${period}`,
      );

      if (response.data.length > 0) {
        const existingAttendance = {};
        const stats = {
          present: 0,
          absent: 0,
          late: 0,
          halfDay: 0,
          onLeave: 0,
        };

        response.data.forEach((record) => {
          existingAttendance[record.studentId._id] = record.status;
          stats[statusToStatKey(record.status)]++;
        });

        setAttendance(existingAttendance);
        setStats(stats);
      } else {
        // No records for this date yet — reset per-student marks back to the
        // all-"present" default too, not just the stats. Otherwise the
        // previously-viewed date's marks stay visually (and, with autoSave,
        // functionally) applied to this date.
        const defaultAttendance = {};
        students.forEach((student) => {
          defaultAttendance[student._id] = "present";
        });
        setAttendance(defaultAttendance);
        setStats({ present: 0, absent: 0, late: 0, halfDay: 0, onLeave: 0 });
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage({ type, text });
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }
    messageTimeoutRef.current = setTimeout(() => {
      setMessage("");
    }, 3000);
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance((prev) => {
      const newAttendance = { ...prev, [studentId]: status };

      // Update stats
      const newStats = { present: 0, absent: 0, late: 0, halfDay: 0, onLeave: 0 };
      Object.values(newAttendance).forEach((s) => {
        newStats[statusToStatKey(s)]++;
      });
      setStats(newStats);

      // Auto-save if enabled
      if (autoSave) {
        saveAttendanceData(newAttendance);
      }

      return newAttendance;
    });
  };

  const saveAttendanceData = async (attendanceData) => {
    try {
      const attendanceArray = students.map((student) => ({
        studentId: student._id,
        status: attendanceData[student._id] || "present",
        remarks: "",
      }));

      await axios.post(`${API_BASE}/attendance/mark`, {
        date: formatLocalDate(selectedDate),
        attendance: attendanceArray,
        period,
      });
    } catch (error) {
      console.error("Error auto-saving:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const attendanceData = students.map((student) => ({
        studentId: student._id,
        status: attendance[student._id] || "present",
        remarks: "",
      }));

      await axios.post(`${API_BASE}/attendance/mark`, {
        date: formatLocalDate(selectedDate),
        attendance: attendanceData,
        period,
      });

      showMessage("Attendance saved successfully!", "success");
    } catch (error) {
      showMessage("Error saving attendance. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const markAll = (status) => {
    const newAttendance = {};
    students.forEach((student) => {
      newAttendance[student._id] = status;
    });
    setAttendance(newAttendance);

    // Update stats
    const newStats = { present: 0, absent: 0, late: 0, halfDay: 0, onLeave: 0 };
    newStats[statusToStatKey(status)] = students.length;
    setStats(newStats);

    if (autoSave) {
      saveAttendanceData(newAttendance);
    }
  };

  const handleQrResolved = (session) => {
    setShowScanner(false);
    if (session.classId !== classInfo?._id) {
      showMessage("That QR code is for a different class.", "error");
      return;
    }
    setPeriod(session.period || 0);
    setSelectedDate(new Date(`${session.date}T00:00:00`));
    showMessage(`Checked in: Period ${session.period}${session.subject ? ` (${session.subject})` : ""}`, "success");
  };

  const generateQr = async () => {
    if (!classInfo) return;
    setGeneratingQr(true);
    try {
      const res = await axios.post(`${API_BASE}/attendance/qr-session`, { classId: classInfo._id, period });
      setQrModal(res.data);
    } catch (error) {
      showMessage(error.response?.data?.error || "Failed to generate QR code", "error");
    } finally {
      setGeneratingQr(false);
    }
  };

  // Filter students based on search
  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.rollNumber?.toString().includes(searchQuery)
  );

  const getAttendanceColor = (status) => {
    switch(status) {
      case 'present': return 'bg-green-100 border-green-300';
      case 'absent': return 'bg-red-100 border-red-300';
      case 'late': return 'bg-yellow-100 border-yellow-300';
      case 'half-day': return 'bg-blue-100 border-blue-300';
      case 'on-leave': return 'bg-purple-100 border-purple-300';
      default: return 'bg-gray-100 border-gray-300';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'present': return 'bg-green-200 text-green-800';
      case 'absent': return 'bg-red-200 text-red-800';
      case 'late': return 'bg-yellow-200 text-yellow-800';
      case 'half-day': return 'bg-blue-200 text-blue-800';
      case 'on-leave': return 'bg-purple-200 text-purple-800';
      default: return 'bg-gray-200 text-gray-800';
    }
  };

  const totalStudents = students.length;
  const attendancePercentage = totalStudents > 0 
    ? Math.round((stats.present / totalStudents) * 100) 
    : 0;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-gray-100 flex items-center justify-center h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 bg-gray-100 min-h-screen">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">
              Mark Attendance
            </h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {/* Class Info and Date Selector */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Class</p>
                <p className="font-medium text-lg">
                  {classInfo?.className} - Section {classInfo?.section}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  dateFormat="yyyy-MM-dd"
                  maxDate={new Date()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Period
                </label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value={0}>Whole Day / Homeroom</option>
                  {periods.length > 0
                    ? periods.map((p) => (
                        <option key={p.periodNumber} value={p.periodNumber}>
                          Period {p.periodNumber} — {p.subject}
                        </option>
                      ))
                    : Array.from({ length: 8 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>Period {n}</option>
                      ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auto-save
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoSave}
                    onChange={(e) => setAutoSave(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-600">
                    {autoSave ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                📷 Scan Class QR
              </button>
              <button
                type="button"
                onClick={generateQr}
                disabled={generatingQr}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
              >
                {generatingQr ? "Generating…" : "🔳 Generate QR for this Period"}
              </button>
            </div>
          </div>

          {/* Statistics Cards with Percentage */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <p className="text-sm text-gray-500">Present</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.present}
              </p>
              <p className="text-xs text-gray-400 mt-1">{attendancePercentage}% attendance</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
              <p className="text-sm text-gray-500">Absent</p>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500">
              <p className="text-sm text-gray-500">Late</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.late}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
              <p className="text-sm text-gray-500">Half Day</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.halfDay}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
              <p className="text-sm text-gray-500">On Leave</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.onLeave}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-indigo-600">
                {totalStudents}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Quick Actions</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => markAll("present")}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded transition"
              >
                ✓ Mark All Present
              </button>
              <button
                onClick={() => markAll("absent")}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition"
              >
                ✕ Mark All Absent
              </button>
              <button
                onClick={() => markAll("late")}
                className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded transition"
              >
                ⏱ Mark All Late
              </button>
              <button
                onClick={() => markAll("half-day")}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition"
              >
                ⌛ Mark All Half Day
              </button>
              <button
                onClick={() => markAll("on-leave")}
                className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded transition"
              >
                🌴 Mark All On Leave
              </button>
            </div>
          </div>

          {/* Message Display */}
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg border-l-4 ${
                message.type === "success"
                  ? "bg-green-50 border-green-500 text-green-700"
                  : "bg-red-50 border-red-500 text-red-700"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Search Bar */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <input
              type="text"
              placeholder="🔍 Search by student name or roll number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {searchQuery && (
              <p className="text-sm text-gray-500 mt-2">
                Found {filteredStudents.length} of {totalStudents} students
              </p>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Student Attendance</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded ${
                  viewMode === 'grid'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                Grid View
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded ${
                  viewMode === 'list'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300'
                }`}
              >
                List View
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {filteredStudents.map((student) => (
                  <div
                    key={student._id}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition ${getAttendanceColor(attendance[student._id])}`}
                  >
                    <div className="mb-3">
                      <p className="text-sm text-gray-600 font-semibold">
                        Roll No: {student.rollNumber}
                      </p>
                      <p className="text-lg font-medium text-gray-800">
                        {student.name}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => handleStatusChange(student._id, 'present')}
                        className={`py-2 px-3 rounded text-sm font-medium transition ${
                          attendance[student._id] === 'present'
                            ? 'bg-green-500 text-white'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        ✓ Present
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(student._id, 'absent')}
                        className={`py-2 px-3 rounded text-sm font-medium transition ${
                          attendance[student._id] === 'absent'
                            ? 'bg-red-500 text-white'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        ✕ Absent
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(student._id, 'late')}
                        className={`py-2 px-3 rounded text-sm font-medium transition ${
                          attendance[student._id] === 'late'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        ⏱ Late
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(student._id, 'half-day')}
                        className={`py-2 px-3 rounded text-sm font-medium transition ${
                          attendance[student._id] === 'half-day'
                            ? 'bg-blue-500 text-white'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        ⌛ Half Day
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(student._id, 'on-leave')}
                        className={`py-2 px-3 rounded text-sm font-medium transition col-span-2 ${
                          attendance[student._id] === 'on-leave'
                            ? 'bg-purple-500 text-white'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        🌴 On Leave
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Roll No
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Student Name
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredStudents.map((student) => (
                        <tr key={student._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {student.rollNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {student.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex gap-1 justify-center">
                              <button
                                type="button"
                                onClick={() => handleStatusChange(student._id, 'present')}
                                className={`px-3 py-1 rounded text-xs font-medium transition ${
                                  attendance[student._id] === 'present'
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-green-200'
                                }`}
                              >
                                P
                              </button>
                              <button
                                type="button"
                                onClick={() => handleStatusChange(student._id, 'absent')}
                                className={`px-3 py-1 rounded text-xs font-medium transition ${
                                  attendance[student._id] === 'absent'
                                    ? 'bg-red-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-red-200'
                                }`}
                              >
                                A
                              </button>
                              <button
                                type="button"
                                onClick={() => handleStatusChange(student._id, 'late')}
                                className={`px-3 py-1 rounded text-xs font-medium transition ${
                                  attendance[student._id] === 'late'
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-yellow-200'
                                }`}
                              >
                                L
                              </button>
                              <button
                                type="button"
                                onClick={() => handleStatusChange(student._id, 'half-day')}
                                className={`px-3 py-1 rounded text-xs font-medium transition ${
                                  attendance[student._id] === 'half-day'
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-blue-200'
                                }`}
                              >
                                H
                              </button>
                              <button
                                type="button"
                                onClick={() => handleStatusChange(student._id, 'on-leave')}
                                className={`px-3 py-1 rounded text-xs font-medium transition ${
                                  attendance[student._id] === 'on-leave'
                                    ? 'bg-purple-500 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-purple-200'
                                }`}
                              >
                                O
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-3 sticky bottom-6">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition"
              >
                {saving ? "Saving..." : "💾 Save Attendance (Ctrl+S)"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showScanner && (
        <QrScannerModal onClose={() => setShowScanner(false)} onResolved={handleQrResolved} />
      )}

      {qrModal && (
        <Modal
          title="Classroom Attendance QR"
          subtitle={`${qrModal.className} - ${qrModal.section} · Period ${qrModal.period} · expires in ${qrModal.expiresInMinutes} min`}
          onClose={() => setQrModal(null)}
        >
          <div className="flex flex-col items-center gap-4">
            <img src={qrModal.qrImage} alt="Attendance QR code" className="w-64 h-64" />
            <a
              href={qrModal.qrImage}
              download={`attendance-qr-${qrModal.className}-${qrModal.section}-p${qrModal.period}.png`}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            >
              Download / Print
            </a>
            <p className="text-xs text-gray-500 text-center">
              Display this in the classroom. Scanning it opens this class/period's attendance screen.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TeacherAttendance;
